import React, { useState, lazy, Suspense } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

// ─── Immediate imports (shown first) ────────────────────────────────────────────
import AuthPage from './components/AuthPage';
import IntroScreen from './components/IntroScreen';
import PrivacyConsent from './components/PrivacyConsent';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';

// ─── Lazy loaded components ─────────────────────────────────────────────────────
const MainApp = lazy(() => import('./components/MainApp'));
const Onboarding = lazy(() => import('./components/Onboarding'));

// ─── Loading Spinner Component ─────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--background)',
      gap: '16px',
    }}>
      <div style={{
        width: '48px',
        height: '48px',
        borderRadius: '12px',
        background: 'var(--foreground)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'pulse 1.5s ease-in-out infinite',
      }}>
        <span style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--primary-foreground)' }}>HR</span>
      </div>
      <p style={{ color: 'var(--muted-foreground)', fontSize: '0.9rem' }}>Loading...</p>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(0.95); }
        }
      `}</style>
    </div>
  );
}

// ─── Keys for localStorage ─────────────────────────────────────────────────────
const INTRO_SEEN_KEY = 'HairRabbit_intro_seen';

function AppContent() {
  const { currentUser, userProfile, profileLoading, profileResolved } = useAuth();
  const [introSeen, setIntroSeen] = useState(() => {
    return localStorage.getItem(INTRO_SEEN_KEY) === 'true';
  });

  // Mark intro as seen and persist to localStorage
  const handleIntroComplete = () => {
    localStorage.setItem(INTRO_SEEN_KEY, 'true');
    setIntroSeen(true);
  };

  // ─── Step 1: Show intro screen for first-time visitors ────────────────────────
  if (!introSeen) {
    return <IntroScreen onComplete={handleIntroComplete} />;
  }

  // ─── Step 2: Not logged in → show login/signup ────────────────────────────────
  if (!currentUser) {
    return <AuthPage />;
  }

  // ─── Step 3: Logged in but profile still loading → show loading spinner ──────
  // This prevents flashing MainApp before we know onboarding status
  if ((!profileResolved && !userProfile) || (profileLoading && !userProfile)) {
    return <LoadingScreen />;
  }

  // If profile fetch failed, we have a fallback profile - show onboarding or main app
  if (!userProfile) {
    return <AuthPage />;
  }

  const privacyConsentRequired =
    userProfile?.privacy_consent_required === true ||
    userProfile?.privacy_policy_accepted === false;

  if (privacyConsentRequired) {
    return <PrivacyConsent />;
  }

  // ─── Step 4: Profile loaded, check onboarding status ──────────────────────────
  // Use !== true to catch false, null, undefined (more robust)
  if (userProfile.onboarding_completed !== true) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <Onboarding onComplete={() => {}} />
      </Suspense>
    );
  }

  // ─── Step 5: Fully onboarded user → show main app ─────────────────────────────
  return (
    <Suspense fallback={<LoadingScreen />}>
      <MainApp />
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
}

function AppRouter() {
  const location = useLocation();

  if (/^\/privacy-policy\/?$/.test(location.pathname)) {
    return <PrivacyPolicyPage />;
  }

  return (
    <AuthProvider>
      <AppContentWrapper />
    </AuthProvider>
  );
}

function AppContentWrapper() {
  return (
    <div className="App">
      <AppContent />
    </div>
  );
}

export default App;
